// src/hooks/audience/useYearStatuses.js
import { useCallback, useEffect, useState } from "react";
import Api from "../../utils/Api";
import { API_ROUTE_AUDIENCE_DATA_INGESTION_SUMMARY } from "../../routes";

// Keyed by displayType too: Desktop and Mobile don't share the same files,
// so a month's status for one must never leak into the other when the user
// switches the Display Type dropdown.
const getKey = (dt, y, m) => `${dt}-${y}-${m}`;

export function useYearStatuses({ year, displayTypeId }) {
  const [monthStatuses, setMonthStatuses] = useState({});
  const [statusesError, setStatusesError] = useState("");

  const fetchYearStatuses = useCallback(async () => {
    if (!displayTypeId) return;
    setStatusesError("");
    try {
      const resp = await Api.post(API_ROUTE_AUDIENCE_DATA_INGESTION_SUMMARY, {
        year,
        displayTypeId,
        take: 50,
      });
      const data = Array.isArray(resp.data) ? resp.data : [];
      const byMonth = {};
      data.forEach((f) => {
        const m = f.periodMonth - 1;
        if (!byMonth[m]) byMonth[m] = [];
        byMonth[m].push(f);
      });
      setMonthStatuses((prev) => {
        const next = { ...prev };
        // Walk every month of the year, not just the ones present in the
        // response: a month with no files for this display type must be
        // reset to idle instead of keeping whatever status it had before
        // (e.g. from the previously selected display type).
        for (let m = 0; m < 12; m += 1) {
          const key = getKey(displayTypeId, year, m);
          const files = byMonth[m];
          if (!files?.length) {
            delete next[key];
            continue;
          }
          const completedCount = files.filter(
            (f) => f.audienceFileStatusName === "Completed",
          ).length;
          const hasInProgress = files.some(
            (f) => f.audienceFileStatusName === "In Progress",
          );
          const hasError = files.some(
            (f) => f.audienceFileStatusName === "Error",
          );
          // Priority: loading > error > done. "loading" wins even if the other
          // file of the month already completed - something is still actively
          // processing. "error" wins over a lone "done": if one of the (up to 2)
          // files for this month is in Error, the month isn't fully OK even
          // though the other one succeeded - it must show red, not green.
          if (hasInProgress) next[key] = "loading";
          else if (hasError) next[key] = "error";
          else if (completedCount >= 1) next[key] = "done";
          else delete next[key];
        }
        return next;
      });
    } catch (err) {
      setStatusesError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to load month statuses.",
      );
    }
  }, [year, displayTypeId]);

  useEffect(() => {
    fetchYearStatuses();
  }, [fetchYearStatuses]);

  const getMonthStatus = useCallback(
    (y, m) => monthStatuses[getKey(displayTypeId, y, m)] || "idle",
    [monthStatuses, displayTypeId],
  );

  const setMonthStatus = useCallback(
    (y, m, status) => {
      setMonthStatuses((prev) => ({
        ...prev,
        [getKey(displayTypeId, y, m)]: status,
      }));
    },
    [displayTypeId],
  );

  return {
    monthStatuses,
    getMonthStatus,
    setMonthStatus,
    statusesError,
    setStatusesError,
    refetchYearStatuses: fetchYearStatuses,
  };
}
